import React, { useEffect } from "react";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/design-system/providers/ThemeToggle";
import { SiteIcon } from "@/components/icons/siteIcons";
import { ZarityMark } from "@/components/icons/ZarityMark";
import {
  consultationSidebarSections,
  iconography,
  type SiteIconKey,
} from "@/design-system/tokens/iconography";
import { cn } from "@/lib/utils";
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
} from "@/components/ui/sidebar";

// Single source of truth for sidebar widths.
// ConsultationLayout imports these to keep the <aside> width in sync.
export const CONSULT_SIDEBAR_WIDTH = "13rem"; // expanded — matches w-52
export const CONSULT_SIDEBAR_WIDTH_ICON = "4rem"; // collapsed — matches w-16

interface PatientInfo {
  full_name?: string;
  zarity_id?: string;
}

export type ConsultationTab =
  | "overview"
  | "symptoms"
  | "medicines"
  | "reports"
  | "family"
  | "lifestyle"
  | "prescription"
  | "nutrition"
  | "treatment-overview";

interface ConsultationSidebarProps {
  patientInfo?: PatientInfo;
  activeTab: ConsultationTab;
  onTabChange: (tab: ConsultationTab) => void;
  onClose: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  userRole?: string;
}

/**
 * ConsultationSidebar — consult-sidebar variant
 *
 * Uses shadcn sidebar primitives inside a floating card that matches the
 * home AppSidebar's visual style. Renders inline (not fixed-position) to fit
 * inside ConsultationLayout's sticky <aside>.
 */
export const ConsultationSidebar = ({
  patientInfo: _patientInfo,
  activeTab,
  onTabChange,
  onClose,
  isCollapsed,
  onToggleCollapse,
  userRole,
}: ConsultationSidebarProps) => {
  const role = userRole?.toLowerCase();
  const ToggleIcon = isCollapsed ? PanelLeftOpen : PanelLeftClose;

  const adviceItem =
    role === "nutritionist"
      ? consultationSidebarSections.nutrition
      : role === "doctor"
        ? consultationSidebarSections.prescription
        : null;

  const sections: {
    label: string;
    items: { id: ConsultationTab; label: string; iconKey: SiteIconKey }[];
  }[] = [
    { label: "EHR", items: [...consultationSidebarSections.ehr] },
    ...(adviceItem ? [{ label: "Advice", items: [adviceItem] }] : []),
  ];

  // SidebarProvider registers a global Ctrl/Cmd+B shortcut to toggle the sidebar.
  // In the consultation room this would conflict with bold in rich-text fields.
  // This capture-phase listener intercepts the shortcut when an editable element
  // is focused so the browser's native bold action still fires, but the
  // SidebarProvider handler does not.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "b") {
        const target = document.activeElement;
        const isEditable =
          target instanceof HTMLElement &&
          (target.tagName === "INPUT" ||
            target.tagName === "TEXTAREA" ||
            target.contentEditable === "true");
        if (isEditable) {
          e.stopImmediatePropagation();
        }
      }
    };
    // Capture phase runs before SidebarProvider's bubble-phase listener.
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, []);

  return (
    // SidebarProvider supplies the useSidebar() context consumed by SidebarMenuButton.
    // open={!isCollapsed} maps external collapse state → sidebar open/closed state.
    // Inline style overrides the provider's default h-svh / overflow-hidden so it
    // fills the parent <aside> instead of taking over the viewport.
    <SidebarProvider
      open={!isCollapsed}
      onOpenChange={() => onToggleCollapse()}
      style={
        {
          "--sidebar-width": CONSULT_SIDEBAR_WIDTH,
          "--sidebar-width-icon": CONSULT_SIDEBAR_WIDTH_ICON,
          height: "100%",
          minHeight: 0,
          overflow: "visible",
        } as React.CSSProperties
      }
    >
      {/*
       * Outer: fills the <aside>, holds group + data attributes for
       * group-data-[collapsible=icon]:* selectors on child primitives.
       * p-2 creates the margin that gives the floating card its inset look.
       */}
      <div
        className="group flex h-full w-full flex-col p-2"
        data-sidebar="sidebar"
        data-variant="consult-sidebar"
        data-state={isCollapsed ? "collapsed" : "expanded"}
        data-collapsible={isCollapsed ? "icon" : ""}
      >
        {/* Floating card — matches AppSidebar's floating variant appearance */}
        <div className="flex h-full flex-col overflow-hidden rounded-xl border border-sidebar-border bg-sidebar text-sidebar-foreground shadow-sm">
          {/* Header — mirrors AppSidebar's animated dual-state header */}
          <SidebarHeader className="gap-2 p-2">
            <div className="relative h-11 transition-[height] duration-200 ease-linear group-data-[collapsible=icon]:h-16">
              {/* Expanded: logo + "Zarity" label + toggle */}
              <div className="absolute inset-0 flex items-center gap-2 transition-all duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:translate-y-1 group-data-[collapsible=icon]:opacity-0">
                <div className="flex flex-1 items-center gap-2 overflow-hidden">
                  <ZarityMark
                    variant="reversed"
                    size={32}
                    className="shrink-0"
                  />
                  <span className="truncate font-heading text-lg font-bold leading-tight tracking-[-0.03em] text-sidebar-foreground">
                    zarity
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 border border-sidebar-border/60 bg-sidebar-accent/40 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  onClick={onToggleCollapse}
                  aria-label="Collapse sidebar"
                >
                  <ToggleIcon className="size-4" />
                </Button>
              </div>

              {/* Collapsed: logo + toggle stacked */}
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-0 transition-all duration-200 ease-linear pointer-events-none -translate-y-1 group-data-[collapsible=icon]:pointer-events-auto group-data-[collapsible=icon]:translate-y-0 group-data-[collapsible=icon]:opacity-100">
                <ZarityMark variant="reversed" size={32} className="shrink-0" />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 border border-sidebar-border/60 bg-sidebar-accent/40 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  onClick={onToggleCollapse}
                  aria-label="Expand sidebar"
                >
                  <ToggleIcon className="size-4" />
                </Button>
              </div>
            </div>
          </SidebarHeader>

          {/* Navigation */}
          <SidebarContent className="px-2 py-1">
            {sections.map((section, index) => (
              <div key={section.label}>
                <SidebarGroup className="gap-0.5 px-0 py-0">
                  <SidebarGroupLabel className="z-caption h-auto px-2 pb-0.5 pt-0.5 text-sidebar-foreground/55">
                    {section.label}
                  </SidebarGroupLabel>
                  <SidebarGroupContent>
                    <SidebarMenu className="gap-0.5">
                      {section.items.map((item) => (
                        <SidebarMenuItem key={item.id}>
                          <SidebarMenuButton
                            tooltip={item.label}
                            isActive={activeTab === item.id}
                            onClick={() => onTabChange(item.id)}
                            className="z-secondary h-8 rounded-md px-2 text-sidebar-foreground data-[active=true]:bg-sidebar-primary/12 data-[active=true]:font-medium data-[active=true]:text-sidebar-primary hover:bg-sidebar-accent/55"
                          >
                            <SiteIcon
                              name={item.iconKey}
                              className={iconography.size.nav}
                            />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
                {index < sections.length - 1 && (
                  <SidebarSeparator className="my-1.5" />
                )}
              </div>
            ))}
          </SidebarContent>

          {/* Exit — CSS-driven collapsed state, no manual isCollapsed check */}
          <SidebarFooter className="gap-1.5 p-2">
            <div className="flex justify-center px-2 group-data-[collapsible=icon]:hidden">
              <ThemeToggle />
            </div>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "w-full h-9 z-button",
                "border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive",
                "group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:justify-center"
              )}
              onClick={onClose}
            >
              <span className="group-data-[collapsible=icon]:hidden">Exit</span>
              <span className="hidden group-data-[collapsible=icon]:inline">
                ✕
              </span>
            </Button>
          </SidebarFooter>
        </div>
      </div>
    </SidebarProvider>
  );
};
